import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import PortalLayout from "@/components/ventrix/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import Pagination from "@/components/ventrix/Pagination";
import { ModeIcon, formatMoney, formatDate, EmptyState } from "@/components/ventrix/atoms";
import { FileText, Send, Inbox } from "lucide-react";

const LIMIT = 50;
const TABS = [
  { v: "new", label: "New" },
  { v: "sent", label: "Sent" },
  { v: "converted", label: "Converted" },
  { v: "dismissed", label: "Dismissed" },
  { v: "all", label: "All" },
];

export default function QuoteRequests({ section = "admin" }) {
  const nav = useNavigate();
  const [status, setStatus] = useState("new");
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [busy, setBusy] = useState("");        // rid currently being actioned
  const [sendFor, setSendFor] = useState(null); // request open in the send dialog

  const load = () => {
    setItems(null);
    api.get(`/v1/quote-requests?status=${status}&limit=${LIMIT}&offset=${offset}`)
      .then(({ data }) => { setItems(data.items || []); setTotal(data.total ?? (data.items || []).length); })
      .catch(() => setItems([]));
  };
  useEffect(() => { load(); }, [status, offset]); // eslint-disable-line

  const pick = (v) => { setStatus(v); setOffset(0); };

  const dismiss = async (rid) => {
    setBusy(rid);
    try {
      await api.post(`/v1/quote-requests/${rid}/dismiss`);
      toast.success("Request dismissed");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  // Open the booking wizard / invoice form prefilled from this quote (all editable).
  const toBooking = (r) => nav("/ops/book", { state: { fromQuote: r } });
  const toInvoice = (r) => nav(section === "ops" ? "/ops/invoices" : "/backoffice/invoices",
    { state: { fromQuote: r } });

  return (
    <PortalLayout section={section}>
      <div className="vtx-eyebrow">/ {section === "ops" ? "Ops" : "Admin"} · Quotes</div>
      <h1 className="mt-2 text-3xl font-black text-[#0E2F76] tracking-tighter inline-flex items-center gap-2"><FileText size={24} /> Quote requests</h1>
      <p className="text-sm text-[#475569] mt-1">Customers submit shipment details from the public site. Prepare a quote — adjust the estimate if needed — and email it to them.</p>

      <div className="mt-6 flex flex-wrap gap-1.5" data-testid="quote-tabs">
        {TABS.map(t => (
          <button key={t.v} onClick={() => pick(t.v)} data-testid={`tab-${t.v}`}
            className={`px-3.5 py-1.5 text-sm font-semibold transition-colors ${status === t.v ? "bg-[#0E2F76] text-white" : "bg-white text-[#0E2F76] border border-[#A9C0E0]/60 hover:border-[#0E2F76]"}`}
            style={{ borderRadius: 2 }}>{t.label}</button>
        ))}
      </div>

      <div className="vtx-card mt-4 overflow-x-auto">
        {items === null && <div className="p-6 space-y-2">{[1, 2, 3].map(i => <div key={i} className="vtx-skeleton h-12" />)}</div>}
        {items && items.length === 0 && (
          <EmptyState icon={Inbox} title="No requests here" hint="Quote requests from the public site land in this queue for your team to price and send." />
        )}
        {items && items.length > 0 && (
          <table className="vtx-table w-full">
            <thead><tr><th>Route</th><th>Mode</th><th>Cargo</th><th>Email</th><th>Estimate</th><th>Submitted</th><th></th></tr></thead>
            <tbody>
              {items.map(r => {
                const inp = r.input || {};
                const c = r.computed || {};
                return (
                  <tr key={r.id} data-testid={`quote-row-${r.id}`}>
                    <td className="whitespace-nowrap"><span className="font-semibold text-[#0E2F76]">{inp.origin}</span> → <span className="font-semibold text-[#0E2F76]">{inp.destination}</span></td>
                    <td><ModeIcon mode={inp.mode} showLabel /></td>
                    <td className="text-xs text-[#475569] capitalize">{inp.cargo_type || "general"} · {inp.pieces || 1} pc · {inp.weight_kg} kg</td>
                    <td className="font-mono text-xs">{r.email}</td>
                    <td className="font-semibold">{formatMoney(c.total, c.currency)}</td>
                    <td className="text-xs text-[#475569] whitespace-nowrap">{formatDate(r.created_at)}</td>
                    <td>
                      {r.status === "dismissed" ? (
                        <span className="text-xs text-[#475569]">Dismissed</span>
                      ) : (
                        <div className="flex flex-col gap-1">
                          <div className="flex flex-wrap gap-x-3 gap-y-1">
                            {!r.shipment_id && (
                              <button className="text-xs text-[#0E2F76] font-bold hover:underline disabled:opacity-40" disabled={busy === r.id}
                                onClick={() => setSendFor(r)} data-testid={`quote-send-${r.id}`}>Prepare &amp; send</button>
                            )}
                            {!r.shipment_id && (
                              <button className="text-xs text-[#0E2F76] font-bold hover:underline disabled:opacity-40" disabled={busy === r.id}
                                onClick={() => toBooking(r)} data-testid={`quote-tobooking-${r.id}`}>Convert to booking</button>
                            )}
                            {!r.invoice_id && (
                              <button className="text-xs text-[#0E2F76] font-bold hover:underline disabled:opacity-40" disabled={busy === r.id}
                                onClick={() => toInvoice(r)} data-testid={`quote-toinvoice-${r.id}`}>Convert to invoice</button>
                            )}
                            <button className="text-xs text-[#d41f26] font-bold hover:underline disabled:opacity-40" disabled={busy === r.id}
                              onClick={() => dismiss(r.id)} data-testid={`quote-dismiss-${r.id}`}>Dismiss</button>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {r.status === "sent" && <span className="text-[10px] font-bold uppercase tracking-wider text-[#166534]">Sent {formatMoney(r.sent_total, c.currency)}</span>}
                            {r.invoice_id && <span className="text-[10px] font-bold uppercase tracking-wider text-[#0E2F76]">Invoiced</span>}
                            {r.shipment_id && <span className="text-[10px] font-bold uppercase tracking-wider text-[#0E2F76]">Booked</span>}
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <Pagination total={total} limit={LIMIT} offset={offset} onChange={setOffset} />
      </div>

      <SendDialog request={sendFor} onClose={() => setSendFor(null)} onSent={() => { setSendFor(null); load(); }} />
    </PortalLayout>
  );
}

function SendDialog({ request, onClose, onSent }) {
  const [total, setTotal] = useState("");
  const [breakdown, setBreakdown] = useState(true);   // itemise surcharges by default
  const [base, setBase] = useState("");
  const [fuel, setFuel] = useState("");
  const [security, setSecurity] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (request) {
      const c = request.computed || {};
      const s = c.surcharges || {};
      setTotal(c.total != null ? String(c.total) : "");
      setBase(c.base != null ? String(c.base) : "");
      setFuel(s.fuel != null ? String(s.fuel) : "");
      setSecurity(s.security != null ? String(s.security) : "");
      setBreakdown(true);
      setNote("");
    }
  }, [request]);

  if (!request) return null;
  const inp = request.input || {};
  const c = request.computed || {};

  const send = async () => {
    setSending(true);
    try {
      const payload = {};
      const t = Number(total);
      if (total !== "" && t > 0) payload.total = t;
      // Surcharge figures are always editable; the toggle only decides whether
      // the customer sees the breakdown.
      payload.include_breakdown = breakdown;
      if (base !== "") payload.base = Math.max(0, Number(base) || 0);
      if (fuel !== "") payload.fuel = Math.max(0, Number(fuel) || 0);
      if (security !== "") payload.security = Math.max(0, Number(security) || 0);
      if (note.trim()) payload.note = note.trim();
      const { data } = await api.post(`/v1/quote-requests/${request.id}/send`, payload);
      if (data.sent) {
        toast.success(`Quote emailed to ${request.email}`);
        onSent();
      } else {
        toast.error(data.message || "The email couldn't be sent.");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const cur = c.currency || "USD";
  return (
    <Dialog open={!!request} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white max-w-lg" data-testid="quote-send-dialog">
        <DialogHeader><DialogTitle>Prepare quote</DialogTitle></DialogHeader>
        <div className="vtx-card p-4 bg-[#F4FEFF] text-sm text-[#334155]">
          <div className="font-semibold text-[#0E2F76]">{inp.origin} → {inp.destination}</div>
          <div className="mt-1 text-xs uppercase tracking-wider text-[#475569]">{String(inp.mode || "").toUpperCase()} · {inp.pieces || 1} pc · {inp.weight_kg} kg · {inp.cargo_type || "general"}</div>
          <div className="mt-2 text-xs text-[#475569]">System estimate: <span className="font-semibold text-[#0E2F76]">{formatMoney(c.total, cur)}</span> · chargeable {c.chargeable_weight_kg} kg · ETA {c.eta_days} days</div>
        </div>
        <p className="text-xs text-[#475569] -mt-1">Sending to <span className="font-mono text-[#0E2F76]">{request.email}</span>. Fields are prefilled from the system estimate — adjust before sending.</p>

        <div className="grid grid-cols-3 gap-3">
          <label className="block">
            <span className="vtx-eyebrow block mb-1.5">Base rate ({cur})</span>
            <input type="number" min="0" step="0.01" className="vtx-input" value={base} onChange={(e) => setBase(e.target.value)} data-testid="send-base" />
          </label>
          <label className="block">
            <span className="vtx-eyebrow block mb-1.5">Fuel ({cur})</span>
            <input type="number" min="0" step="0.01" className="vtx-input" value={fuel} onChange={(e) => setFuel(e.target.value)} data-testid="send-fuel" />
          </label>
          <label className="block">
            <span className="vtx-eyebrow block mb-1.5">Security ({cur})</span>
            <input type="number" min="0" step="0.01" className="vtx-input" value={security} onChange={(e) => setSecurity(e.target.value)} data-testid="send-security" />
          </label>
        </div>

        <label className="flex items-center gap-2 select-none">
          <input type="checkbox" checked={breakdown} onChange={(e) => setBreakdown(e.target.checked)} data-testid="send-breakdown-toggle" />
          <span className="text-sm font-semibold text-[#0E2F76]">Show the surcharge breakdown in the customer's quote</span>
        </label>

        <label className="block">
          <span className="vtx-eyebrow block mb-1.5">Quote total, all-in ({cur})</span>
          <input type="number" min="0" step="0.01" className="vtx-input" value={total} onChange={(e) => setTotal(e.target.value)} data-testid="send-total" />
        </label>
        <label className="block">
          <span className="vtx-eyebrow block mb-1.5">Note to customer (optional)</span>
          <textarea className="vtx-input" rows={3} maxLength={2000} placeholder="e.g. Rate includes customs clearance. Valid for 14 days." value={note} onChange={(e) => setNote(e.target.value)} data-testid="send-note" />
        </label>
        <DialogFooter>
          <button className="vtx-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="vtx-btn-primary inline-flex items-center gap-2" onClick={send} disabled={sending} data-testid="send-confirm">
            <Send size={14} /> {sending ? "Sending…" : "Send quote"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
